/**
 * <PaymentCollectionDayFlow> — choisir sa date de prélèvement (template, L0.1).
 *
 * Deux temps, comme la modale legacy (`ChangePaymentCollectionDayModal.js:127-197`) : la feuille
 * des jours possibles, puis la confirmation nominative avant d'écrire.
 *
 * **Contrôlé** (règle du 18/09 : l'état d'interface reste dans le composant, l'état métier arrive
 * en props). Ici tout est métier — l'étape, le jour choisi, l'envoi en cours, l'échec — donc tout
 * arrive en props et rien n'est possédé : la machine vit dans `usePaymentCollectionDayAdapter`.
 * C'est ce qui fait que chaque état s'ouvre seul dans une story, sans cliquer.
 */
import React from "react"

import { ActionSheet } from "../../molecules/ActionSheet"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { elide } from "../../../ports/primary/ui/formatters/childFormatter"

/** `days` : la feuille des jours possibles. `confirm` : la question avant d'écrire. */
export type PaymentCollectionDayStep = "days" | "confirm"

export type PaymentCollectionDayFlowProps = {
    visible: boolean
    onClose: () => void
    /** Étape en cours — défaut `days`. */
    step?: PaymentCollectionDayStep
    /** Jour choisi, en attente de confirmation. */
    pendingDay?: number | null
    /** Jour actuellement prélevé — coché dans la feuille. */
    currentDay?: number | null
    /** Prénom de l'élève — la confirmation le nomme. Absent → « votre date de prélèvement ». */
    studentFirstName?: string | null
    /** Jours proposés — l'écran passe la liste que le back-office accepte (`PAYMENT_COLLECTION_DAYS`). */
    days: number[]
    onSelectDay: (day: number) => void
    onConfirm: () => void
    /** Retour en arrière depuis la confirmation (legacy : la feuille est déjà fermée). */
    onCancelConfirm: () => void
    /** Envoi en cours : le bouton le dit et ne répond plus. */
    submitting?: boolean
    /** Échec de l'écriture, déjà mis en phrase par les ports. */
    errorMessage?: string | null
    testID?: string
}

export function PaymentCollectionDayFlow(props: PaymentCollectionDayFlowProps) {
    const {
        visible,
        onClose,
        step = "days",
        pendingDay = null,
        currentDay,
        studentFirstName,
        days,
        onSelectDay,
        onConfirm,
        onCancelConfirm,
        submitting = false,
        errorMessage = null,
        testID,
    } = props

    const confirmTitle = studentFirstName
        ? `Voulez-vous changer la date de prélèvement de l'abonnement ${elide(studentFirstName)}\u00a0?`
        : "Voulez-vous changer votre date de prélèvement ?"

    // L'échec prend la place de la phrase de rappel : la question ne change pas, seule la raison
    // de la re-poser change.
    const confirmMessage = errorMessage ?? (pendingDay != null ? `Nouvelle date : le ${pendingDay} du mois` : undefined)

    return (
        <>
            <ActionSheet
                testID={testID}
                visible={visible && step === "days"}
                onClose={onClose}
                // Choisir un jour passe à la confirmation : la feuille se cache par l'étape, pas par
                // `onClose`, qui fermerait tout le parcours avant que la question ne s'affiche.
                closeOnSelect={false}
                title="Ma date de prélèvement"
                items={days.map((day) => ({
                    icon: day === currentDay ? ("check" as const) : ("calendar-alt" as const),
                    label: `Le ${day} du mois`,
                    onPress: () => onSelectDay(day),
                }))}
            />

            <ConfirmDialog
                visible={visible && step === "confirm"}
                title={confirmTitle}
                message={confirmMessage}
                confirmLabel={submitting ? "Confirmation…" : "Confirmer"}
                cancelLabel="Annuler"
                danger={false}
                onConfirm={onConfirm}
                onCancel={onCancelConfirm}
            />
        </>
    )
}
